import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ImageInfoPopover from './ImageInfoPopover';
import useImageActions from './useImageActions';

const PLACEHOLDER_TEXT_MAX_LENGTH = 120;
const PLACEHOLDER_ELLIPSIS = '...';
const IMAGE_PLACEHOLDER_MIN_HEIGHT = 120;

const ImageComponent = ({ node, selected }: NodeViewProps) => {
  const src = typeof node.attrs?.src === 'string' ? node.attrs.src : '';
  const altText = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
  const titleText =
    typeof node.attrs?.title === 'string' ? node.attrs.title : '';
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const handleInfoOpen = useCallback((event: MouseEvent) => {
    setInfoAnchorEl(event.currentTarget as HTMLElement);
  }, []);
  const handleInfoClose = useCallback(() => {
    setInfoAnchorEl(null);
  }, []);
  const imageActions = useImageActions({
    src,
    altText,
    titleText,
    onInfo: handleInfoOpen
  });
  const placeholderText = (titleText || altText || '').trim();
  const normalizedPlaceholderText =
    placeholderText.length > PLACEHOLDER_TEXT_MAX_LENGTH
      ? `${placeholderText.slice(
          0,
          Math.max(0, PLACEHOLDER_TEXT_MAX_LENGTH - PLACEHOLDER_ELLIPSIS.length)
        )}${PLACEHOLDER_ELLIPSIS}`
      : placeholderText;
  const showError = hasError || !src;
  const showPlaceholder = !showError && !isLoaded;

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setInfoAnchorEl(null);
  }, [src]);

  return (
    <NodeViewWrapper
      contentEditable={false}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          lineHeight: 0,
          '&:hover .image-actions': {
            opacity: 1
          },
          border: selected ? '1px solid #21965f' : '1px solid transparent',
          borderRadius: 1,
          overflow: 'hidden',
          ...(showPlaceholder || showError
            ? {
                minWidth:
                  document?.querySelector('.editorContent')?.clientWidth || 200,
                minHeight: IMAGE_PLACEHOLDER_MIN_HEIGHT
              }
            : {})
        }}
        data-parent={node.attrs['data-parent']}
      >
        {(showPlaceholder || showError) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              px: 2,
              textAlign: 'center',
              backgroundColor: 'rgba(250, 250, 250, 0.96)',
              border: '1px dashed',
              borderColor: showError ? 'error.light' : 'grey.300',
              borderRadius: 1,
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            {!showError && <CircularProgress size={18} />}
            <Typography
              variant="caption"
              color={showError ? 'error.main' : 'text.secondary'}
            >
              {showError ? 'Image failed to load' : 'Loading image...'}
            </Typography>
            {normalizedPlaceholderText && (
              <Typography variant="caption" color="text.disabled">
                {normalizedPlaceholderText}
              </Typography>
            )}
          </Box>
        )}
        {src && (
          <img
            src={src}
            alt={altText}
            title={titleText}
            className={node.attrs.class}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
            style={{
              maxWidth: '100%',
              minWidth: '100%',
              height: 'auto',
              display: 'block',
              opacity: showPlaceholder || showError ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out'
            }}
          />
        )}

        <Box
          className="image-actions"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 1,
            padding: '4px',
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out'
          }}
        >
          {imageActions.map((action) => (
            <Tooltip key={action.title} title={action.title}>
              <IconButton
                size="small"
                onClick={action.handler}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <ImageInfoPopover
          anchorEl={infoAnchorEl}
          onClose={handleInfoClose}
          description={altText}
        />
      </Box>
    </NodeViewWrapper>
  );
};

export default ImageComponent;
